// ---------------------------------------------------------------------------
// SwitchPayerModal  — capture card -► call /client/switch-to-business
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Portal,
    Tooltip,
    IconButton,
    CircularProgress,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

import CreditCardForm from './parts/CreditCardForm';
import PaymentService from '../../Services/payment.service';
import styles from './SwitchPayerModal.module.scss';

const logSwitchModal = (...args) => {
    if (typeof console !== 'undefined' && console.log) {
        console.log('[Xtram SwitchPayerModal]', ...args);
    }
};

const validate = (v) => {
    const e = {}, n = v.cardNumber.replace(/\s+/g, '');
    if (!/^\d{16}$/.test(n)) e.cardNumber = 'Invalid';
    const mm = +v.month, yy = +v.year;
    if (!mm || mm < 1 || mm > 12) e.month = 'MM';
    if (!yy || yy < 23 || yy > 99) e.year = 'YY';
    if (!/^\d{3}$/.test(v.cvv)) e.cvv = 'CVV';
    return e;
};

const SwitchPayerModal = ({ open, onClose, caseId, onSuccess }) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();

    const [busy, setBusy] = useState(false);
    const [ok, setOk] = useState(false);
    const [error, setError] = useState('');
    const [vals, setVals] = useState({ cardNumber: '', month: '', year: '', cvv: '', holder: '' });
    const [errs, setErrs] = useState({});
    const [existingCard, setExistingCard] = useState(null);
    const [checkingCard, setCheckingCard] = useState(false);

    // When opening (or switching client), reset UI state and load card for this caseId only.
    // Without clearing existingCard first, a card from a previous client/session stays truthy and
    // the auto-update effect can call switch-to-business for the wrong case or hide the add-card form.
    useEffect(() => {
        if (!open || !caseId) {
            logSwitchModal('effect skip (open or caseId empty)', { open, caseId });
            return undefined;
        }

        logSwitchModal('open + caseId: reset state and fetch payment method', { caseId });

        setOk(false);
        setError('');
        setBusy(false);
        setErrs({});
        setVals({ cardNumber: '', month: '', year: '', cvv: '', holder: '' });
        setExistingCard(null);

        let cancelled = false;
        (async () => {
            setCheckingCard(true);
            try {
                const cardData = await PaymentService.client.getPaymentMethod(caseId);
                if (!cancelled) {
                    // API returns 200 with data: null when there is no saved card (does not throw)
                    const normalized = cardData || null;
                    const hasLast4 = !!(normalized && (normalized.last4 || normalized.Last4));
                    logSwitchModal('getPaymentMethod done', {
                        caseId,
                        cancelled,
                        cardData: normalized,
                        hasLast4
                    });
                    setExistingCard(normalized);
                } else {
                    logSwitchModal('getPaymentMethod ignored (effect cancelled)', { caseId });
                }
            } catch (e) {
                logSwitchModal('getPaymentMethod error', { caseId, error: e });
                if (!cancelled) setExistingCard(null);
            } finally {
                if (!cancelled) setCheckingCard(false);
            }
        })();

        return () => {
            cancelled = true;
            logSwitchModal('effect cleanup (cancel in-flight)', { caseId });
        };
    }, [open, caseId]);

    // Auto-proceed only when we have a real saved card for the current fetch result
    const hasExistingCard =
        existingCard &&
        (existingCard.last4 || existingCard.Last4);

    const handleChange = e => {
        const { name, value } = e.target;
        const fmt = s => s.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim();
        setVals(v => ({ ...v, [name]: name === 'cardNumber' ? fmt(value) : value }));
        setErrs(e => ({ ...e, [name]: '' }));
    };

    const handleSave = async () => {
        const v = validate(vals);
        if (Object.keys(v).length) { setErrs(v); return; }

        setBusy(true); setError('');
        const dto = {
            CaseId: caseId,
            Card: {
                UserId: caseId,
                CardNumber: vals.cardNumber.replace(/\s+/g, ''),
                ExpiryMonth: +vals.month,
                ExpiryYear: +vals.year,
                CVV: vals.cvv,
                CardHolderId: vals.holder
            }
        };
        try {
            logSwitchModal('switchToBusiness new card request', { CaseId: dto.CaseId });
            await PaymentService.client.switchToBusiness(dto);
            logSwitchModal('switchToBusiness new card success');
            setOk(true);
            setTimeout(() => { onSuccess(); onClose(); }, 1200);
        } catch (ex) {
            logSwitchModal('switchToBusiness new card error', ex?.message || ex);
            setError(ex.message || t('PaymentFailed'));
        }
        finally { setBusy(false); }
    };

    const handleAutoUpdate = useCallback(async () => {
        setBusy(true);
        setError('');
        const dto = { CaseId: caseId, UseSavedCard: true };
        try {
            logSwitchModal('switchToBusiness UseSavedCard request', dto);
            await PaymentService.client.switchToBusiness(dto);
            logSwitchModal('switchToBusiness UseSavedCard success');
            setOk(true);
            setTimeout(() => { onSuccess(); onClose(); }, 1200);
        } catch (ex) {
            logSwitchModal('switchToBusiness UseSavedCard error', ex?.message || ex);
            setError(ex.message || t('PaymentFailed'));
        } finally {
            setBusy(false);
        }
    }, [caseId, onSuccess, onClose, t]);

    useEffect(() => {
        if (!hasExistingCard || busy || ok || checkingCard) {
            return undefined;
        }
        logSwitchModal('scheduling auto switch-to-business in 1s (UseSavedCard)', { caseId });
        const id = setTimeout(() => {
            logSwitchModal('auto switch-to-business firing', { caseId });
            handleAutoUpdate();
        }, 1000);
        return () => clearTimeout(id);
    }, [hasExistingCard, busy, ok, checkingCard, handleAutoUpdate, caseId]);

    const getModalTitle = () => {
        if (checkingCard) return t('CheckingCard');
        if (hasExistingCard) return t('UpdateCard');
        return t('AddCard');
    };

    const getSuccessMessage = () => {
        if (hasExistingCard) return t('CardUpdatedSuccess');
        return t('CardSavedSuccess');
    };

    if (!open) return null;

    return (
        <Portal>
            <div
                className={styles.overlay}
                onClick={onClose}
            >
                <div dir={dir} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modal}>
                        <div className={styles.scroll}>
                            <div className={styles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={onClose} className={styles.closeIcon}>
                                        <CancelIcon className={styles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{ok ? t('PayerSwitched') : getModalTitle()}</div>
                                <div />
                            </div>

                            <div className={styles.body}>
                                {checkingCard ? (
                                    <div className={styles.loading}>
                                        <CircularProgress className={styles.spinner} size={24} />
                                        <span>{t('CheckingExistingCard')}</span>
                                    </div>
                                ) : ok ? (
                                    <div className={styles.alertSuccess}>
                                        <CheckIcon fontSize="small" />
                                        <span>{getSuccessMessage()}</span>
                                    </div>
                                ) : (
                                    <>
                                        {hasExistingCard && (
                                            <div className={styles.alertInfo}>
                                                <InfoOutlinedIcon fontSize="small" />
                                                <span>
                                                    {t('ExistingCardFound', {
                                                        last4: existingCard.last4 ?? existingCard.Last4,
                                                        expiryMonth: existingCard.expiryMonth ?? existingCard.ExpiryMonth,
                                                        expiryYear: existingCard.expiryYear ?? existingCard.ExpiryYear
                                                    })}
                                                </span>
                                            </div>
                                        )}
                                        <CreditCardForm
                                            values={vals}
                                            onChange={handleChange}
                                            disabled={busy}
                                            errors={errs}
                                            direction={dir}
                                        />
                                        {error && (
                                            <div className={styles.alertError}>
                                                <ErrorIcon fontSize="small" />
                                                <span>{error}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {!ok && !checkingCard && (
                                <div className={styles.footer}>
                                    <button type="button" className={styles.cancel} onClick={onClose}>
                                        {t('Close')}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.save}
                                        onClick={handleSave}
                                        disabled={busy}
                                    >
                                        {busy ? (
                                            <CircularProgress size={20} sx={{ color: 'var(--xtram-white)' }} />
                                        ) : null}
                                        {hasExistingCard ? t('UpdateCard') : t('SaveCard')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

SwitchPayerModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    caseId: PropTypes.string.isRequired
};

export default SwitchPayerModal;
